/**
 * EnvironmentalSeal — the permanent, locked branding element of
 * The Archive.
 *
 * A single oversized instance of the official Archive seal
 * (`/logo-lockup.png`) rendered as a screen-blended, softly masked
 * environmental element inside a `position: relative` ancestor. It is
 * strictly decorative: `aria-hidden`, `pointer-events-none`, and sits
 * behind the content layer at `-z-10`.
 *
 * The seal asset is *never* redesigned, simplified, recoloured, or
 * replaced. Only the intensity of its presence changes by page type,
 * per the locked branding system:
 *
 *   intensity  | scale of homepage seal | typical page
 *   -----------|------------------------|-----------------------------
 *   "hero"     | 100 %                  | Homepage hero
 *   "archive"  |  45 %                  | Archive index / listing
 *   "topic"    |  25 %                  | Entity / topic pages
 *   "reading"  |  12 %                  | Dossier reading pages
 *
 * Admin / Operations pages MUST NOT render this component.
 *
 * A numeric `intensity` prop (0.0 – 1.0) is also accepted for fine
 * tuning without redefining a preset.
 */
import React from "react";

const BASE_OPACITY = 0.11;

const INTENSITY_PRESETS = {
  hero:    1.00,   // 0.110
  archive: 0.45,   // 0.050
  topic:   0.25,   // 0.028
  reading: 0.12,   // 0.013
};

const SEAL_URL = `${process.env.PUBLIC_URL || ""}/logo-lockup.png`;

/**
 * @param {object} props
 * @param {"hero"|"archive"|"topic"|"reading"|number} [props.intensity="hero"]
 *   Preset key or explicit 0-1 opacity multiplier applied to
 *   BASE_OPACITY.
 * @param {object} [props.positionStyle]
 *   Optional overrides for top/left/transform if a page needs the seal
 *   anchored differently. Defaults to the locked hero anchor.
 * @param {string} [props.className]
 * @param {object} [props.style]
 */
export const EnvironmentalSeal = ({
  intensity = "hero",
  positionStyle,
  className = "",
  style = {},
  ...rest
}) => {
  const multiplier =
    typeof intensity === "number"
      ? Math.max(0, Math.min(1, intensity))
      : (INTENSITY_PRESETS[intensity] ?? 1);

  const opacity = BASE_OPACITY * multiplier;

  const defaultPosition = {
    top: "43%",
    left: "22%",
    transform: "translate(-50%, -50%)",
    width: "min(1200px, 104vh, 100vw)",
    height: "min(1200px, 104vh, 100vw)",
  };

  return (
    <div
      aria-hidden="true"
      data-testid="environmental-seal"
      data-intensity={typeof intensity === "string" ? intensity : "custom"}
      className={`absolute -z-10 pointer-events-none select-none ${className}`}
      style={{
        ...defaultPosition,
        ...(positionStyle || {}),
        backgroundImage: `url(${SEAL_URL})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        opacity,
        mixBlendMode: "screen",
        filter: "blur(0.15px) contrast(1.1) saturate(0.6)",
        maskImage:
          "radial-gradient(ellipse 58% 68% at 50% 50%, black 45%, transparent 82%)",
        WebkitMaskImage:
          "radial-gradient(ellipse 58% 68% at 50% 50%, black 45%, transparent 82%)",
        ...style,
      }}
      {...rest}
    />
  );
};

export default EnvironmentalSeal;
